// ─────────────────────────────────────────────────────────────
// Legal pages. These describe what this website actually does:
// two forms (estimate + job application) that email Pepla through
// Resend, hosting on Netlify, and no analytics or advertising
// cookies at the time of writing. If you add analytics, ad pixels,
// call tracking or online payments, update these pages to match.
//
// Drafted for Pepla Roofing and reviewed for
// accuracy against the site's behavior — not legal advice. Have an
// Arizona attorney review before relying on them.
// ─────────────────────────────────────────────────────────────

import { analytics, socialFeed, brand } from "./site";

export const LEGAL_UPDATED = "September 27, 2026";

// The analytics paragraph follows the live configuration, so the policy
// is accurate whether or not GA4 / call tracking is switched on.
const analyticsText = analytics.ga4Id
  ? [
      "With your permission, we use Google Analytics to understand how visitors use this website, such as which pages are viewed and whether someone taps to call, text or send a form. Google Analytics sets cookies and receives your IP address, which Google shortens before storage. We do not send your name, phone number, email or address to Google Analytics.",
      ...(analytics.callTrackingScript
        ? ["With the same permission, we use a call-tracking service that may show a different phone number on this site so we can tell which pages lead to calls. Calls to that number are forwarded to us and may be recorded by the provider for quality and training."]
        : []),
      "Analytics only turns on if you choose Allow on the cookie banner. You can change your mind at any time by clearing this site's data in your browser, and the banner will ask again. We do not use advertising cookies or sell your information.",
    ]
  : [
      "We do not currently use advertising cookies, analytics cookies or session-recording tools on this website. If we add analytics or advertising tools in the future, we will update this policy and, where required, ask for your consent first.",
    ];

// Facebook's Page Plugin is an iframe served by Meta, so it is disclosed
// for as long as it is switched on in src/data/site.js.
const embedText = socialFeed.plugin?.enabled
  ? [
      "Our homepage embeds Facebook's page plugin so you can see our latest posts. That panel is loaded from Facebook and can set Facebook cookies and receive your IP address and the page you are viewing, whether or not you have a Facebook account. We do not receive any personal information from it. Facebook's handling of that data is covered by Meta's own privacy policy.",
    ]
  : [];

export const legalDocs = [
  {
    slug: "privacy-policy",
    title: "Privacy Policy",
    seoTitle: "Privacy Policy | Pepla Roofing",
    seoDescription:
      "How Pepla Roofing collects, uses and protects information submitted through peplaroofing.com.",
    intro:
      `This policy explains what information ${brand.legalName} ("Pepla," "we," "us") collects through peplaroofing.com, how we use it, and the choices you have. It applies to this website. It does not cover information collected offline, or by other companies whose sites we link to.`,
    sections: [
      {
        h: "Who we are",
        body: [
          `${brand.legalName}, Phoenix, Arizona.${brand.address ? ` ${brand.address}.` : ""}${brand.rocNumber ? ` Arizona ROC license #${brand.rocNumber}.` : ""} Phone ${brand.phone}. Email ${brand.email}.`,
        ],
      },
      {
        h: "Information you give us",
        body: ["We collect information only when you choose to send it. Specifically:"],
        list: [
          "Estimate requests: first and last name, phone number, email address, the project address (street, city, ZIP), which services you're interested in, any message you write, and whether you opted in to marketing text messages.",
          "Job applications: your name, phone, email, mailing address, the role you want, years of experience, employment type, availability, whether you're authorized to work in the US, whether you hold a valid driver's license, any link you provide, anything you write about yourself, and the resume file you upload.",
          "Calls, texts and emails: if you contact us directly, we keep what you send so we can respond and keep a record of the work.",
        ],
      },
      {
        h: "Information collected automatically",
        body: [
          "Our website is hosted by Netlify, which keeps standard server logs. Those logs can include your IP address, browser type, the pages requested and the time of the request. They are used for security and to keep the site running.",
          ...analyticsText,
          ...embedText,
        ],
      },
      {
        h: "How we use your information",
        list: [
          "To respond to your estimate request, schedule an inspection, and prepare a quote.",
          "To perform work you hire us for, including ordering materials and coordinating subcontractors.",
          "To review and respond to job applications.",
          "To send service messages about your project, such as scheduling and follow-up.",
          "To send marketing messages, but only if you asked us to.",
          "To keep business records, handle warranty questions, and meet legal or licensing obligations.",
        ],
      },
      {
        h: "Text messages",
        body: [
          "We do not send marketing text messages, and this website does not collect consent for them. Our office line, (602) 834-4707, receives calls only. We reach you about a project by phone or email, using the details you gave us. See our SMS Terms for details.",
        ],
      },
      {
        h: "Who we share it with",
        body: ["We do not sell your personal information, and we do not share it for cross-context behavioral advertising. We share it only as follows:"],
        list: [
          `Service providers who run parts of our business: our website host (Netlify), our email delivery provider (Resend), our business email provider (Microsoft 365)${analytics.ga4Id ? ", Google Analytics (only with your permission)" : ""}, and the software we use to manage customers and estimates.`,
          "Subcontractors and suppliers when needed to quote, order materials for, or complete your project.",
          "Insurers, lenders, or your real estate agent, but only when you ask us to.",
          "Government agencies or others when required by law, or to establish or defend legal claims.",
        ],
      },
      {
        h: "Resumes",
        body: [
          "Resumes submitted through the Join Our Team page are emailed to our hiring team and are not stored on this website. We keep applications while we consider you for current or future openings. Ask us at contact@peplaroofing.com to have yours deleted.",
        ],
      },
      {
        h: "Project photos",
        body: [
          "We often photograph work in progress and completed projects, and we may use those photos on our website, in proposals, or on social media. Photos show the property, not your name or address, unless you give us permission. If you'd rather we didn't photograph your project, tell us and we won't.",
        ],
      },
      {
        h: "Reviews and testimonials",
        body: [
          "We may publish reviews customers post publicly, such as on Google, and may quote them on this website. We identify reviewers by first name and last initial, and we don't edit the substance of what someone wrote.",
        ],
      },
      {
        h: "How long we keep information",
        body: [
          "We keep customer and project records as long as needed to perform the work, support warranties, and meet tax, insurance and licensing requirements. We keep inquiries that don't become projects only as long as they're useful, and applications as described above.",
        ],
      },
      {
        h: "Security",
        body: [
          "Our website uses HTTPS, and form submissions are transmitted over encrypted connections. No method of transmission or storage is perfectly secure, so we can't guarantee absolute security. Please don't send sensitive information such as Social Security numbers or financial account numbers through the website forms.",
        ],
      },
      {
        h: "Your choices and rights",
        body: [
          "You can ask us to correct or delete information we hold about you, ask what we have, or opt out of marketing at any time. Email contact@peplaroofing.com or call (602) 834-4707.",
          "Residents of states with comprehensive privacy laws (including California, Colorado, Connecticut, Texas, Virginia and others) may have additional rights to access, correct, delete, or obtain a copy of their personal information, and to appeal a denial. We honor those requests regardless of where you live. We will not discriminate against you for exercising these rights.",
          "To submit a request, contact us using the details above and tell us what you'd like. We may need to verify your identity before acting on a request.",
        ],
      },
      {
        h: "Children",
        body: [
          "This website is meant for adults. We do not knowingly collect personal information from children under 13. If you believe a child has sent us information, contact us and we will delete it.",
        ],
      },
      {
        h: "Other websites",
        body: [
          "Our site links to other websites, such as Google, Facebook and manufacturers' sites. We don't control them, and their privacy practices are their own.",
        ],
      },
      {
        h: "Changes to this policy",
        body: [
          "If we change this policy, we'll post the new version here and update the date at the top. Material changes will be described in plain language.",
        ],
      },
      {
        h: "Contact us",
        body: [
          `${brand.legalName}, Phoenix, Arizona.${brand.address ? ` ${brand.address}.` : ""} Email ${brand.email}. Phone ${brand.phone}.`,
        ],
      },
    ],
  },

  {
    slug: "terms-of-service",
    title: "Terms of Service",
    seoTitle: "Terms of Service | Pepla Roofing",
    seoDescription:
      "The terms that apply to your use of peplaroofing.com, including estimates, submissions, disclaimers and governing law.",
    intro:
      `These terms apply to your use of peplaroofing.com, operated by ${brand.legalName} ("Pepla," "we," "us"). By using the site you agree to them. If you don't agree, please don't use the site.`,
    sections: [
      {
        h: "About our work",
        body: [
          `${brand.legalName} is a roofing contractor serving Phoenix and Maricopa County, Arizona.${brand.rocNumber ? ` Arizona ROC license #${brand.rocNumber}.` : ""} Work we perform is governed by the written contract you sign for that project, not by this website. If anything here conflicts with your signed contract, the contract controls.`,
        ],
      },
      {
        h: "Estimates and pricing",
        body: [
          "Information on this site is general and is not an offer, a bid or a guarantee of price or availability. Estimates are free and carry no obligation. A final price depends on an on-site inspection, measurements, material selection, access, and conditions that can only be confirmed once work begins, such as damaged decking discovered during a tear-off. Any promotion described on this site, including a free upgrade offer, is subject to the terms we give you at the time and may be changed or ended at any time.",
        ],
      },
      {
        h: "Using this website",
        body: ["You agree not to:"],
        list: [
          "Use the site for any unlawful purpose, or to submit false or misleading information.",
          "Submit another person's information without their permission.",
          "Attempt to gain unauthorized access to the site, its forms, or the systems behind them.",
          "Scrape, copy or reuse our content, photos or written materials without written permission.",
          "Upload files containing malware or anything you don't have the right to share.",
        ],
      },
      {
        h: "What you send us",
        body: [
          "When you submit a form, you confirm the information is accurate and that you're authorized to provide it, including the property address for an estimate. If you send us photos, documents or a resume, you keep ownership of them and give us permission to use them to respond to you and evaluate your request.",
        ],
      },
      {
        h: "Our content",
        body: [
          `The text, layout, photos and design of this site belong to ${brand.legalName} or are used with permission. Manufacturer names and logos are the property of their owners and are shown to identify products we install; they don't imply those companies endorse or sponsor us.`,
        ],
      },
      {
        h: "Third-party information",
        body: [
          "Some pages summarize public information, such as building rules, state laws or local programs, and link to sources. Those summaries are for general information only and can become outdated. Rules change and vary by property. Confirm anything that matters to your project with the relevant city, county or agency, and don't treat this site as legal, real estate, engineering, insurance or tax advice.",
        ],
      },
      {
        h: "Disclaimers",
        body: [
          "The website is provided \"as is\" and \"as available.\" To the fullest extent allowed by law, we disclaim all warranties about the site, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We don't promise the site will always be available, error-free, or that information on it is complete or current. This paragraph applies to the website. It does not limit any workmanship or manufacturer warranty that comes with work we perform for you.",
        ],
      },
      {
        h: "Limitation of liability",
        body: [
          `To the fullest extent allowed by law, ${brand.legalName} is not liable for indirect, incidental, special or consequential damages arising out of your use of this website. Nothing here limits liability that can't be limited under Arizona law.`,
        ],
      },
      {
        h: "Links to other sites",
        body: [
          "We link to other websites for convenience. We don't control them and aren't responsible for their content or practices.",
        ],
      },
      {
        h: "Privacy",
        body: ["Our Privacy Policy explains how we handle information you send through this site, and is part of these terms."],
      },
      {
        h: "Governing law",
        body: [
          "These terms are governed by the laws of the State of Arizona, without regard to conflict-of-laws rules. Any dispute about this website will be brought in the state or federal courts located in Maricopa County, Arizona.",
        ],
      },
      {
        h: "Changes",
        body: [
          "We may update these terms. The version posted here, with the date at the top, is the one that applies.",
        ],
      },
      {
        h: "Contact",
        body: [
          `${brand.legalName}, Phoenix, Arizona.${brand.address ? ` ${brand.address}.` : ""} Email ${brand.email}. Phone ${brand.phone}.`,
        ],
      },
    ],
  },

  {
    slug: "sms-terms",
    title: "SMS Terms & Conditions",
    seoTitle: "SMS Terms & Conditions | Pepla Roofing",
    seoDescription:
      "Pepla Roofing does not operate a text-message program. Our office line takes calls only.",
    intro:
      "Pepla Roofing does not currently operate a text-message program. We do not send marketing texts, and this website does not collect consent for them.",
    sections: [
      {
        h: "Our office number takes calls only",
        body: [
          `Our office line, ${brand.phone}, is answered for calls. It is not set up to receive text messages, so a text sent to it will not reach us. Please call it, use the estimate form on this site, or email ${brand.email} instead.`,
        ],
      },
      {
        h: "How we contact you about a project",
        body: [
          "When you ask us for an estimate or hire us, we contact you by phone or email using the details you gave us — scheduling, arrival windows, questions from the crew and follow-up. That is ordinary contact about work you asked for, not a marketing program.",
        ],
      },
      {
        h: "If you opted in before",
        body: [
          `If you previously agreed to receive marketing texts from us, you can be removed at any time. Call ${brand.phone} or email ${brand.email} and we will take you off the list. Replying STOP to any message you may already have received also works.`,
        ],
      },
      {
        h: "If this changes",
        body: [
          "If we start a text-message program in future, these terms will be rewritten to describe it before any message is sent, and consent will be collected separately and explicitly at that time.",
        ],
      },
      {
        h: "Contact",
        body: [
          `${brand.legalName}, Phoenix, Arizona.${brand.address ? ` ${brand.address}.` : ""} Phone ${brand.phone}. Email ${brand.email}.`,
        ],
      },
    ],
  },

  {
    slug: "accessibility",
    title: "Accessibility Statement",
    seoTitle: "Accessibility Statement | Pepla Roofing",
    seoDescription:
      "Pepla Roofing's commitment to keeping peplaroofing.com usable for everyone, and how to report a problem.",
    intro:
      "We want every homeowner in the Valley to be able to use this website, including people who use screen readers, keyboard navigation, or magnification.",
    sections: [
      {
        h: "What we aim for",
        body: [
          "We build this site with the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA as our target. That includes readable color contrast, text that scales, labels on every form field, alternative text on meaningful images, keyboard-reachable navigation and forms, and respect for the \"reduce motion\" setting on your device.",
        ],
      },
      {
        h: "Where we may fall short",
        body: [
          "Accessibility is ongoing work, and parts of the site may not fully meet that standard yet. Content from third parties, such as embedded maps or review widgets, is outside our control.",
        ],
      },
      {
        h: "What we have tested",
        body: [
          "In September 2026 every page of this site was checked against WCAG 2.1 Level AA with automated tooling and by hand. That covered color contrast on text and interface elements, keyboard-only navigation, visible focus, a skip-to-content link, form labels and errors, image alternative text, page language, and layout down to a 320-pixel-wide screen and at 200% browser zoom. Issues found in that review were fixed.",
          "Automated tools catch only part of what matters, so this is not a guarantee that the site is free of barriers, and it is not a certification — there is no such thing for websites. It is a statement of the standard we hold ourselves to and of the work we have actually done.",
        ],
      },
      {
        h: "Tell us about a problem",
        body: [
          "If something on this site is hard to use, we want to hear about it and we'll fix it. Call (602) 834-4707 or email contact@peplaroofing.com with the page and what happened. If you can't use the site to reach us, call and we'll take your request over the phone.",
        ],
      },
    ],
  },
];

export const getLegalDoc = (slug) => legalDocs.find((d) => d.slug === slug);
